import React from 'react';
import { styled } from 'baseui';
import { Spinner } from 'baseui/spinner';
import { useS3Image, UseS3ImageOptions } from '../hooks/useS3Image';

export interface S3ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  imageKey: string | null | undefined;
  alt: string;
  s3Options?: UseS3ImageOptions;
  fallbackComponent?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  showLoading?: boolean;
  onImageLoad?: () => void;
  onImageError?: (error: string | null) => void;
}

const ImageContainer = styled('div', {
  position: 'relative',
  display: 'inline-block',
  width: '100%',
  height: '100%',
});

const StyledImage = styled('img', {
  width: '100%',
  display: 'block',
});

const LoadingContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: '60px',
  backgroundColor: '#f5f5f5',
  borderRadius: '4px',
});

const ErrorContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: '60px',
  backgroundColor: '#f5f5f5',
  borderRadius: '4px',
  color: '#666',
  fontSize: '14px',
  textAlign: 'center',
  padding: '8px',
});

/**
 * S3Image component that automatically handles presigned URLs for S3 images
 * @param imageKey - The S3 key or existing URL
 * @param alt - Alt text for the image
 * @param s3Options - Options for S3 image processing
 * @param fallbackComponent - Component to show when image fails or no imageKey
 * @param loadingComponent - Custom loading component
 * @param showLoading - Whether to show loading state
 * @param onImageLoad - Callback when image loads successfully
 * @param onImageError - Callback when image fails to load
 * @param ...props - Standard img element props
 */
export const S3Image: React.FC<S3ImageProps> = ({
  imageKey,
  alt,
  s3Options,
  fallbackComponent,
  loadingComponent,
  showLoading = true,
  onImageLoad,
  onImageError,
  style,
  className,
  ...props
}) => {
  const { imageUrl, isLoading, error } = useS3Image(imageKey, s3Options);

  // Handle image load success
  const handleImageLoad = () => {
    onImageLoad?.();
  };

  // Handle image load error (different from URL fetch error)
  const handleImageError = () => {
    onImageError?.(error);
  };

  // Notify parent about URL fetch errors
  React.useEffect(() => {
    if (error) {
      onImageError?.(error);
    }
  }, [error, onImageError]);

  // Show loading state
  if (isLoading && showLoading) {
    return (
      <ImageContainer style={style} className={className}>
        {loadingComponent || (
          <LoadingContainer>
            <Spinner $size={24} />
          </LoadingContainer>
        )}
      </ImageContainer>
    );
  }

  // Show error state or fallback
  if (error || !imageUrl) {
    return (
      <ImageContainer style={style} className={className}>
        {fallbackComponent || (
          <ErrorContainer>
            {error ? 'Failed to load image' : 'No image available'}
          </ErrorContainer>
        )}
      </ImageContainer>
    );
  }

  // Show the actual image
  return (
    <ImageContainer className={className}>
      <StyledImage
        {...props}
        src={imageUrl}
        alt={alt}
        style={style}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </ImageContainer>
  );
}; 